import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import Button from "components/Buttons/Button";
import EmptyState from "components/EmptyState";
import MetricChart from "components/MetricChart/MetricChart";
import PeriodSelector, {
  DateRangeValue,
  PeriodPreset,
} from "components/PeriodSelector/PeriodSelector";
import Sidebar from "components/Sidebar";
import StatisticCard from "components/StatisticCard/StatisticCard";
import { useNotify } from "context/NotificationContext";
import { fetchStatistics } from "services/statisticService/statisticService";
import type { StatisticResponse } from "services/statisticService/statisticService.types";
import { logger } from "utils/logger";
import {
  Board,
  ContentWrapper,
  EmptyStateWrapper,
  HeaderContainer,
  Hint,
  LoadingContainer,
  StatisticsPageContainer,
  Subtitle,
  Title,
  Toolbar,
} from "./StatisticsPage.styles";
import type {
  StatisticMetricConfig,
  StatisticMetricId,
  StatisticsBucket,
} from "./StatisticsPage.types";
import { buildFlipKeyframes, hasVisibleFlip, type FlipRect } from "./statisticsAnimation";
import {
  aggregateStatistics,
  formatMetricAxisValue,
  formatMetricValue,
  formatPeriodNote,
  getEarliestStatisticDate,
  getMetricDelta,
  getMetricValue,
  getPresetRange,
} from "./statistics.utils";

const METRICS: StatisticMetricConfig[] = [
  { id: "income", title: "Доход", chartKind: "area", showAverage: true },
  { id: "joined", title: "Новые ученики", chartKind: "bar", showAverage: true },
  { id: "days", title: "Дни подписки", chartKind: "line", showAverage: true },
  { id: "total", title: "Всего учеников", chartKind: "area" },
];

const INITIAL_LAYOUT: StatisticMetricId[] = ["income", "joined", "days", "total"];
const SWAP_DURATION_MS = 560;
const SWAP_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

type CardNodes = Partial<Record<StatisticMetricId, HTMLDivElement>>;
type CardRects = Partial<Record<StatisticMetricId, FlipRect>>;
type PendingSwap = {
  ids: [StatisticMetricId, StatisticMetricId];
  rects: CardRects;
};

const bucketValue = (metric: StatisticMetricId, bucket: StatisticsBucket) => {
  if (metric === "income") return bucket.totalIncome;
  if (metric === "joined") return bucket.newUsers;
  if (metric === "days") return bucket.subscriptionDays;
  return bucket.totalUsers;
};

const StatisticsPage: React.FC = () => {
  const toast = useNotify();
  const [statistics, setStatistics] = useState<StatisticResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("365");
  const [period, setPeriod] = useState<DateRangeValue>(() =>
    getPresetRange("365", null, new Date()),
  );
  const [layout, setLayout] = useState<StatisticMetricId[]>(INITIAL_LAYOUT);
  const cardNodesRef = useRef<CardNodes>({});
  const pendingSwapRef = useRef<PendingSwap | null>(null);
  const activeAnimationsRef = useRef<Animation[]>([]);
  const isAnimatingRef = useRef(false);

  const loadStatistics = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      setStatistics(await fetchStatistics());
    } catch (error) {
      logger.error("Ошибка при загрузке статистики:", error);
      setHasError(true);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить статистику",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadStatistics();
  }, [loadStatistics]);

  useEffect(() => () => {
    activeAnimationsRef.current.forEach((animation) => animation.cancel());
  }, []);

  const earliestDate = useMemo(() => getEarliestStatisticDate(statistics), [statistics]);
  const maxDate = useMemo(() => new Date(), []);
  const aggregated = useMemo(
    () => aggregateStatistics(statistics, period),
    [statistics, period],
  );

  const handlePresetChange = (preset: Exclude<PeriodPreset, "custom">) => {
    setPeriodPreset(preset);
    setPeriod(getPresetRange(preset, earliestDate, maxDate));
  };

  const captureCardRects = useCallback((): CardRects => {
    const rects: CardRects = {};
    METRICS.forEach(({ id }) => {
      const node = cardNodesRef.current[id];
      if (!node) return;
      const rect = node.getBoundingClientRect();
      rects[id] = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
    });
    return rects;
  }, []);

  const promoteMetric = (metricId: StatisticMetricId) => {
    if (isAnimatingRef.current) return;

    const index = layout.indexOf(metricId);
    const bigIndex = layout.length - 1;
    if (index < 0 || index === bigIndex) return;

    const currentBigMetricId = layout[bigIndex];
    pendingSwapRef.current = {
      ids: [metricId, currentBigMetricId],
      rects: captureCardRects(),
    };

    setLayout((current) => {
      const currentIndex = current.indexOf(metricId);
      const currentBigIndex = current.length - 1;
      if (currentIndex < 0 || currentIndex === currentBigIndex) return current;
      const next = [...current];
      [next[currentIndex], next[currentBigIndex]] = [next[currentBigIndex], next[currentIndex]];
      return next;
    });
  };

  useLayoutEffect(() => {
    const pendingSwap = pendingSwapRef.current;
    if (!pendingSwap) return;
    pendingSwapRef.current = null;

    activeAnimationsRef.current.forEach((animation) => animation.cancel());
    activeAnimationsRef.current = [];

    const animatedNodes: HTMLDivElement[] = [];
    const animations: Animation[] = [];

    pendingSwap.ids.forEach((metricId) => {
      const node = cardNodesRef.current[metricId];
      const from = pendingSwap.rects[metricId];
      if (!node || !from) return;

      const currentRect = node.getBoundingClientRect();
      const to: FlipRect = {
        left: currentRect.left,
        top: currentRect.top,
        width: currentRect.width,
        height: currentRect.height,
      };
      if (!hasVisibleFlip(from, to)) return;

      node.style.zIndex = "3";
      animatedNodes.push(node);
      animations.push(
        node.animate(buildFlipKeyframes(from, to), {
          duration: SWAP_DURATION_MS,
          easing: SWAP_EASING,
          fill: "both",
        }),
      );
    });

    if (!animations.length) {
      isAnimatingRef.current = false;
      return;
    }

    isAnimatingRef.current = true;
    activeAnimationsRef.current = animations;

    void Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      animations.forEach((animation) => animation.cancel());
      animatedNodes.forEach((node) => {
        node.style.zIndex = "";
      });
      if (activeAnimationsRef.current === animations) {
        activeAnimationsRef.current = [];
        isAnimatingRef.current = false;
      }
    });
  }, [layout]);

  const renderMetricCard = (metricId: StatisticMetricId, variant: "small" | "big") => {
    const config = METRICS.find((metric) => metric.id === metricId)!;
    const value = getMetricValue(metricId, statistics, period);
    const delta = getMetricDelta(metricId, statistics, period);
    const chartData = aggregated.buckets.map((bucket) => ({
      label: bucket.label,
      value: bucketValue(metricId, bucket),
    }));

    return (
      <StatisticCard
        key={metricId}
        ref={(node) => {
          if (node) {
            cardNodesRef.current[metricId] = node;
          } else {
            delete cardNodesRef.current[metricId];
          }
        }}
        id={metricId}
        title={config.title}
        value={formatMetricValue(metricId, value)}
        delta={delta}
        variant={variant}
        onActivate={variant === "small" ? () => promoteMetric(metricId) : undefined}
      >
        <MetricChart
          data={chartData}
          kind={config.chartKind}
          expanded={variant === "big"}
          showAverage={Boolean(config.showAverage)}
          formatValue={(chartValue) => formatMetricValue(metricId, chartValue)}
          formatAxisValue={(chartValue) => formatMetricAxisValue(metricId, chartValue)}
          ariaLabel={`${config.title}: динамика за выбранный период`}
        />
      </StatisticCard>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </LoadingContainer>
      );
    }

    if (hasError) {
      return (
        <EmptyStateWrapper>
          <EmptyState
            title="Статистика не загрузилась"
            description="Попробуйте повторить запрос."
            action={
              <Button variant="secondary" size="md" onClick={loadStatistics}>
                Повторить
              </Button>
            }
          />
        </EmptyStateWrapper>
      );
    }

    if (!statistics.length) {
      return (
        <EmptyStateWrapper>
          <EmptyState
            title="Статистики пока нет"
            description="После первых покупок здесь появится динамика учеников, дохода и подписок."
          />
        </EmptyStateWrapper>
      );
    }

    return (
      <>
        <Toolbar>
          <PeriodSelector
            value={periodPreset}
            range={period}
            maxDate={maxDate}
            onPresetChange={handlePresetChange}
            onCustomApply={(range) => {
              setPeriodPreset("custom");
              setPeriod(range);
            }}
          />
          <Hint>Нажмите на панель, чтобы развернуть её</Hint>
        </Toolbar>

        <Board>
          {layout.map((metricId, index) =>
            renderMetricCard(
              metricId,
              index === layout.length - 1 ? "big" : "small",
            ),
          )}
        </Board>
      </>
    );
  };

  return (
    <StatisticsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <Title>Статистика</Title>
          {!loading && !hasError && statistics.length > 0 && (
            <Subtitle>{formatPeriodNote(period, aggregated.granularity)}</Subtitle>
          )}
        </HeaderContainer>
        {renderContent()}
      </ContentWrapper>
    </StatisticsPageContainer>
  );
};

export default StatisticsPage;
